import { useMemo, useState } from 'react';

import { LoaderFunctionArgs } from '@remix-run/node';
import { useSearchParams } from '@remix-run/react';
import CustomPagination from '~/components/custom-pagination';
import { Home } from '~/components/images/Home';
import Layout from '~/components/layout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { getAuctionHouseListWithoutPagination } from '~/services/api/auction-houses';
import { getAuctionsList } from '~/services/api/auctions';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { AuctionStatus } from '~/types';
import { Filter } from 'lucide-react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

import { AuctionItem } from '../_index/auctions';
import FilterSideBar from '../../components/filter-side-bar';

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { user } = await validateUser(request);

  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const auctionDate = url.searchParams.get('date') || undefined;
  const auctionType = url.searchParams.get('type') || undefined;
  const selectedAuctionHouses = url.searchParams.getAll('seller') || [];

  const [auctions, categories, auctionHouses] = await Promise.all([
    getAuctionsList({
      limit,
      page,
      status: AuctionStatus.APPROVED,
      ...(auctionDate && { auctionDate: new Date(auctionDate?.replace('+', '/')) }),
      ...(auctionType && { auctionType }),
      auctionHouses: selectedAuctionHouses,
    }),
    getCategoryList(),
    getAuctionHouseListWithoutPagination(),
  ]);

  const userId = params.userId;

  return typedjson({
    userId,
    user,
    auctions,
    categories,
    auctionHouses,
  });
};

export default function Auctions() {
  const data = useTypedLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState(false);

  const { auctions, auctionHouses, categories } = data;
  const memoizedAuctionsData = useMemo(() => auctions.list, [auctions.list]);

  const currentPage = data.auctions.page;
  const totalPages = Math.ceil(data.auctions.totalCount / data.auctions.limit);

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbPage>
            <BreadcrumbLink href="/products" className="text-sm font-normal">
              Auctions
            </BreadcrumbLink>
          </BreadcrumbPage>
        </BreadcrumbList>
      </Breadcrumb>

      <div>
        <div className="flex flex-row items-center justify-between">
          <div>
            <p className="text-xl font-medium text-black">Auctions</p>
            <p className="text-xs font-normal text-slate-500">{auctions.totalCount} auctions found</p>
          </div>
          <Button variant={'ghost'} className="rounded-full md:hidden" onClick={() => setOpenFilter((prev) => !prev)}>
            <Filter className="text-gray-600" width={22} height={22} />
          </Button>
        </div>
        <div className="my-4 h-[1px] bg-slate-200" />
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div
          // className="col-span-1"
          className={openFilter ? `col-span-3 lg:col-span-1` : `col-span-3 hidden lg:col-span-1 lg:flex`}
        >
          <FilterSideBar type="auction" auctionHouses={auctionHouses} categories={categories} />
        </div>
        <div className="col-span-3 flex flex-col gap-10">
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {memoizedAuctionsData.map((auction) => (
              <AuctionItem
                auction={auction as unknown as Awaited<ReturnType<typeof getAuctionsList>>['list'][0]}
                userId={data.user?.id}
                key={auction.id}
              />
            ))}
          </div>
          <CustomPagination
            limit={auctions.limit}
            totalCount={auctions.totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevPage={() => {
              searchParams.set('page', (currentPage - 1).toString());
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
            onNextPage={() => {
              searchParams.set('page', (currentPage + 1).toString());
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
            onPageChange={(page) => {
              searchParams.set('page', page);
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
            onLimitChange={(limit) => {
              searchParams.set('limit', limit);
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
          />
        </div>
      </div>
    </Layout>
  );
}
